/**
 * The wave director (spec §7): headless, seeded and allocation-free per step (D-014). It decides when a
 * wave starts and where its ships appear; the sim (G13) turns its `SpawnRequests` into `spawnShip` calls.
 *
 * Schedule: wave 1 starts `intervalSeconds` after the director's first step; each later wave starts
 * `intervalSeconds` after the previous one is cleared. Waves 1..N come from `waves.json`; after that the
 * `repeat.waves` cycle forever, each full cycle stepping every pilot `repeat.escalate` profiles up the
 * profile list (rookie → veteran → ace), capped at `repeat.cap`.
 */
import { emitEvent, EV, type EventRing } from '../combat/events'
import { rotateVec, type Quat, type Vec3 } from '../core/math'
import { logger } from '../core/logger'
import { nextFloat, range, type Rng } from '../core/rng'
import type { AiData, WavesData } from '../data/schemas'
import { lookRotation } from './steering'

/** Candidate positions tried inside the spawn cone before the search widens to every direction. */
const CONE_ATTEMPTS = 24
/** Total candidate positions tried per ship. */
const MAX_ATTEMPTS = 48

/** Preallocated output buffer: the ships the sim must spawn this tick (SoA, `count` valid entries). */
export interface SpawnRequests {
  count: number
  readonly capacity: number
  /** Index into `GameData.ships`. */
  defIndex: Int32Array
  /** Index into `GameData.aiProfiles.profiles`, escalation applied. */
  profile: Uint8Array
  /** Float64 world position (D-011). */
  x: Float64Array
  y: Float64Array
  z: Float64Array
  /** Spawn rotation: local −Z faces the player. */
  qx: Float32Array
  qy: Float32Array
  qz: Float32Array
  qw: Float32Array
}

export function createSpawnRequests(capacity: number): SpawnRequests {
  return {
    count: 0,
    capacity,
    defIndex: new Int32Array(capacity),
    profile: new Uint8Array(capacity),
    x: new Float64Array(capacity),
    y: new Float64Array(capacity),
    z: new Float64Array(capacity),
    qx: new Float32Array(capacity),
    qy: new Float32Array(capacity),
    qz: new Float32Array(capacity),
    qw: new Float32Array(capacity),
  }
}

export interface WaveDirector {
  /** Live wave data (dev-panel tunable). */
  waves: WavesData
  /** Number of AI profiles: escalation never indexes past the last one. */
  profileCount: number
  /** The last wave started, 1-based; 0 before the first. */
  wave: number
  /** Sim time the next wave starts; -1 while unscheduled (before the first step, or while a wave is alive). */
  nextAt: number
  /** True from a wave's start until all its enemies are gone. */
  active: boolean
  /** The largest ship count of any wave: size `SpawnRequests` with it. */
  maxShips: number
}

export function createWaveDirector(waves: WavesData, profiles: AiData): WaveDirector {
  let maxShips = 0
  for (const w of waves.waves) maxShips = Math.max(maxShips, w.ships.length)
  return { waves, profileCount: profiles.profiles.length, wave: 0, nextAt: -1, active: false, maxShips }
}

/** Index into `waves.waves` of 1-based wave `n`. */
export function waveSourceIndex(director: WaveDirector, n: number): number {
  const count = director.waves.waves.length
  if (n <= count) return n - 1
  const cycle = director.waves.repeat.waves
  return cycle[(n - count - 1) % cycle.length] - 1
}

/** Profile steps added to every pilot of 1-based wave `n` (0 for the scripted waves). */
export function waveEscalation(director: WaveDirector, n: number): number {
  const count = director.waves.waves.length
  if (n <= count) return 0
  const repeat = director.waves.repeat
  return (Math.floor((n - count - 1) / repeat.waves.length) + 1) * repeat.escalate
}

/** `base` stepped up by `steps`, never past the cap (a base already above the cap keeps its profile). */
export function escalateProfile(director: WaveDirector, base: number, steps: number): number {
  const cap = Math.min(Math.max(director.waves.repeat.capIndex, base), director.profileCount - 1)
  return Math.min(base + steps, cap)
}

export type InsideFieldFn = (x: number, y: number, z: number) => boolean

// Module scratch (D-014).
const f: Vec3 = { x: 0, y: 0, z: -1 }
const r: Vec3 = { x: 0, y: 0, z: 0 }
const u: Vec3 = { x: 0, y: 0, z: 0 }
const rot: Quat = { x: 0, y: 0, z: 0, w: 1 }

/** Unit basis around the player's forward (degenerate forward → −Z). */
function buildBasis(forward: Vec3): void {
  const len = Math.sqrt(forward.x * forward.x + forward.y * forward.y + forward.z * forward.z)
  if (len < 1e-9) {
    f.x = 0
    f.y = 0
    f.z = -1
  } else {
    f.x = forward.x / len
    f.y = forward.y / len
    f.z = forward.z / len
  }
  // The look rotation of f has f as its −Z, right as +X and up as +Y.
  lookRotation(f.x, f.y, f.z, rot)
  rotateVec(rot, 1, 0, 0, r)
  rotateVec(rot, 0, 1, 0, u)
}

/**
 * Writes request `k`: a position `spawn.distance` from the player inside `spawn.cone` of its forward and
 * outside the field (seeded retries; after CONE_ATTEMPTS the cone opens to every direction), facing the player.
 */
function placeShip(director: WaveDirector, k: number, player: Vec3, inside: InsideFieldFn, rng: Rng, out: SpawnRequests): void {
  const spawn = director.waves.spawn
  let x = 0
  let y = 0
  let z = 0
  let placed = false
  for (let attempt = 0; attempt < MAX_ATTEMPTS && !placed; attempt++) {
    const cone = attempt < CONE_ATTEMPTS ? spawn.cone : Math.PI
    // Uniform over the spherical cap of half-angle `cone`.
    const cosT = 1 - nextFloat(rng) * (1 - Math.cos(cone))
    const sinT = Math.sqrt(Math.max(0, 1 - cosT * cosT))
    const phi = range(rng, 0, Math.PI * 2)
    const d = range(rng, spawn.distance[0], spawn.distance[1])
    const cx = Math.cos(phi) * sinT
    const cy = Math.sin(phi) * sinT
    x = player.x + (f.x * cosT + r.x * cx + u.x * cy) * d
    y = player.y + (f.y * cosT + r.y * cx + u.y * cy) * d
    z = player.z + (f.z * cosT + r.z * cx + u.z * cy) * d
    placed = !inside(x, y, z)
  }
  if (!placed) logger.warn(`Wave ${director.wave}: no spawn point outside the field after ${MAX_ATTEMPTS} tries; using the last one`)
  out.x[k] = x
  out.y[k] = y
  out.z[k] = z
  lookRotation(player.x - x, player.y - y, player.z - z, rot)
  out.qx[k] = rot.x
  out.qy[k] = rot.y
  out.qz[k] = rot.z
  out.qw[k] = rot.w
}

/**
 * One director step (spec §4.2 step 8). `enemiesAlive` counts living raiders; `playerPos` (float64 world)
 * and `playerForward` set the spawn cone. Clears `requests` and fills it when a wave starts, emitting
 * `waveStart` (amount = wave number) into `events`. Returns the wave number started this step, or 0.
 */
export function stepWaves(
  director: WaveDirector,
  enemiesAlive: number,
  now: number,
  playerPos: Vec3,
  playerForward: Vec3,
  isInsideField: InsideFieldFn,
  rng: Rng,
  requests: SpawnRequests,
  events: EventRing,
): number {
  requests.count = 0
  if (director.active) {
    if (enemiesAlive > 0) return 0
    director.active = false
    director.nextAt = now + director.waves.intervalSeconds
  }
  if (director.nextAt < 0) director.nextAt = now + director.waves.intervalSeconds
  if (now < director.nextAt) return 0

  const n = director.wave + 1
  const ships = director.waves.waves[waveSourceIndex(director, n)].ships
  const steps = waveEscalation(director, n)
  director.wave = n
  director.active = true
  director.nextAt = -1
  buildBasis(playerForward)
  const count = Math.min(ships.length, requests.capacity)
  if (count < ships.length) logger.warn(`Wave ${n}: ${ships.length} ships but SpawnRequests holds ${requests.capacity}`)
  for (let k = 0; k < count; k++) {
    requests.defIndex[k] = ships[k].defIndex
    requests.profile[k] = escalateProfile(director, ships[k].profileIndex, steps)
    placeShip(director, k, playerPos, isInsideField, rng, requests)
  }
  requests.count = count
  emitEvent(events, EV.waveStart, 0, 0, 0, 0, 0, 0, -1, -1, 0, n)
  return n
}
